/**
 * 字幕翻译API
 */
import { request } from '@/shared/api/request'

// 字幕翻译相关接口
export interface SubtitleTranslationRequest {
  target_language: string
}

export interface SubtitleTranslationResponse {
  project_id: number
  target_language: string
  status: string
  message: string
}

export interface SubtitleTranslationProgress {
  project_id: number
  target_language: string
  status: string // pending, extracting_hot_words, translating, completed, failed
  progress: number // 0-100
  current_step: string
  total_steps: number
  completed_steps: number
  message: string
  error_message?: string
  hot_words_extracted: boolean
  hot_words_count: number
  subtitles_translated: number
  total_subtitles: number
}

export interface HotWordExtractionRequest {
  target_language: string
  top_n?: number
  force_extract?: boolean
  manual_hot_word?: {
    original_word: string
    translated_word: string
  }
}

export interface HotWordExtractionResponse {
  project_id: number
  hot_words: HotWordResponse[]
  total_count: number
  ai_suggested_count: number
  from_dictionary_count: number
  success: boolean
  message: string
}

export interface HotWordResponse {
  id: number
  original_word: string
  translated_word: string
  language_pair: string
  status: string // ai_suggested, confirmed, edited
  frequency: number
  is_deleted: boolean
  created_at: string
  updated_at: string
}

export interface TranslatedSubtitleResponse {
  id: number
  start_time: number
  end_time: number
  original_text: string
  translated_text?: string
  translated_text_multiline?: string
  language: string
  order_index: number
  character_count: number
}

export interface TranslationSaveRequest {
  target_language: string
  auto_save?: boolean
}

export interface TranslationStatistics {
  project_id: number
  target_language: string
  original_characters: number
  translated_characters: number
}

export interface SubtitleExportRequest {
  target_language: string
  format?: string // srt, webvtt
  version?: string // single_line, multi_line, both
  subtitle_type?: string // original, translated
}

export interface SubtitleExportResponse {
  project_id: number
  target_language: string
  format: string
  files: Array<{
    version: string
    filename: string
    url: string
    size: number
  }>
}

/**
 * 开始字幕翻译
 */
export const startSubtitleTranslation = (
  projectId: number,
  data: SubtitleTranslationRequest
): Promise<SubtitleTranslationResponse> => {
  return request.post(`/subtitle-translation/projects/${projectId}/start-translation`, data)
}

/**
 * 获取翻译进度
 */
export const getTranslationProgress = (
  projectId: number,
  targetLanguage: string
): Promise<SubtitleTranslationProgress> => {
  return request.get(`/subtitle-translation/projects/${projectId}/translation-progress`, {
    params: { target_language: targetLanguage }
  })
}

/**
 * 提取字幕热词（使用统一热词API）
 */
export const extractSubtitleHotWords = (
  projectId: number,
  data: HotWordExtractionRequest
): Promise<HotWordExtractionResponse> => {
  return request.post(`/hot-words/projects/${projectId}/extract-hot-words`, data, {
    timeout: 120000 // 热词提取需要更长时间，设置为120秒
  })
}

/**
 * 获取翻译后的字幕
 */
export const getTranslatedSubtitles = (
  projectId: number,
  targetLanguage: string
): Promise<{
  project_id: number
  target_language: string
  subtitles: TranslatedSubtitleResponse[]
  total_count: number
}> => {
  return request.get(`/subtitle-translation/projects/${projectId}/translated-subtitles`, {
    params: { target_language: targetLanguage }
  })
}

/**
 * 更新字幕翻译
 */
export const updateSubtitleTranslation = (
  projectId: number,
  subtitleId: number,
  translatedText: string,
  translatedTextMultiline?: string
): Promise<{
  subtitle_id: number
  translated_text: string
  translated_text_multiline: string
  updated_at: string
}> => {
  return request.put(`/subtitle-translation/projects/${projectId}/subtitles/${subtitleId}/translation`, null, {
    params: {
      translated_text: translatedText,
      translated_text_multiline: translatedTextMultiline
    }
  })
}

/**
 * 保存翻译进度
 */
export const saveTranslationProgress = (
  projectId: number,
  data: TranslationSaveRequest
): Promise<{
  project_id: number
  saved_at: string
  subtitle_count: number
  character_count: number
  message: string
}> => {
  return request.post(`/subtitle-translation/projects/${projectId}/save-translation`, data)
}

/**
 * 获取翻译统计信息
 */
export const getTranslationStatistics = (
  projectId: number,
  targetLanguage: string
): Promise<TranslationStatistics> => {
  return request.get(`/subtitle-translation/projects/${projectId}/translation-statistics`, {
    params: { target_language: targetLanguage }
  })
}

/**
 * 导出翻译后的字幕文件
 */
export const exportTranslatedSubtitles = (
  projectId: number,
  data: SubtitleExportRequest
): Promise<any> => {
  return request.post(`/subtitle-translation/projects/${projectId}/export-subtitles`, null, {
    params: data
  })
}

/**
 * 完成翻译任务
 */
export const completeTranslationTask = (
  projectId: number,
  targetLanguage: string,
  completionMessage?: string
): Promise<any> => {
  return request.post(`/subtitle-translation/projects/${projectId}/complete-translation`, null, {
    params: {
      target_language: targetLanguage,
      completion_message: completionMessage
    }
  })
}

// 导出API对象（兼容旧代码）
export const subtitleTranslationApi = {
  startSubtitleTranslation,
  getTranslationProgress,
  extractSubtitleHotWords,
  getTranslatedSubtitles,
  updateSubtitleTranslation,
  saveTranslationProgress,
  getTranslationStatistics,
  exportTranslatedSubtitles,
  completeTranslationTask
}

export default subtitleTranslationApi

